import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import { Calendar } from "@repo/ui/components/calendar";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@repo/ui/components/card";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/dialog";
import { Field, FieldGroup, FieldLabel } from "@repo/ui/components/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@repo/ui/components/input-group";
import { Skeleton } from "@repo/ui/components/skeleton";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { Clock2Icon, Loader2Icon, PencilLineIcon, TrashIcon } from "lucide-react";

import { Counter, StaticDuration } from "@/components/time-tracking/counter";

export const Route = createFileRoute("/_authed/")({
  head: () => ({ meta: [{ title: "Time Tracker" }] }),
  component: RouteComponent,
});

function RouteComponent() {
  const ongoingSession = useQuery(api.timeTrackingSimple.getOngoingSession);
  const startSession = useMutation(api.timeTrackingSimple.startSession);
  const stopSession = useMutation(api.timeTrackingSimple.stopSession).withOptimisticUpdate(
    (localStore) => {
      localStore.setQuery(api.timeTrackingSimple.getOngoingSession, {}, null);
    },
  );
  const [mutating, startMutation] = React.useTransition();

  const targetDate: Date | undefined = !ongoingSession
    ? undefined
    : new Date(ongoingSession.startedAt);

  return (
    <div className="container mx-auto flex flex-1 flex-col gap-4 p-4 lg:max-w-4xl">
      <Card className="overflow-hidden">
        <CardContent className="flex flex-col items-center gap-4 rounded-2xl border bg-black/40 px-6 py-6">
          <div className="flex items-center justify-center text-center font-mono text-4xl leading-none font-semibold tracking-tight sm:text-5xl lg:text-[3.5rem]">
            <Counter startedAt={targetDate} />
          </div>
          <Button
            className="mx-auto w-full gap-1.5 text-base font-medium sm:h-10 sm:w-auto sm:px-5"
            variant={ongoingSession ? "destructive" : "outline"}
            onClick={() =>
              startMutation(async () =>
                ongoingSession
                  ? void (await stopSession())
                  : void (await startSession({ startedAt: Date.now() })),
              )
            }
            size="default"
            disabled={mutating}
          >
            {mutating && (
              <>
                <Loader2Icon className="size-4 animate-spin" />
              </>
            )}

            {ongoingSession ? "Stop" : "Start"}
          </Button>
        </CardContent>
      </Card>
      <RecentSession />
    </div>
  );
}

function RecentSession() {
  const confirm = useConfirmationDialog();

  const deleteSession = useMutation(api.timeTrackingSimple.deleteSession).withOptimisticUpdate(
    (localStore, args) => {
      for (const query of localStore.getAllQueries(api.timeTrackingSimple.listMySessions)) {
        if (!query.value) continue;

        localStore.setQuery(api.timeTrackingSimple.listMySessions, query.args, {
          ...query.value,
          page: query.value.page.filter((session) => session._id !== args.sessionId),
        });
      }
    },
  );

  const { results, status, loadMore } = usePaginatedQuery(
    api.timeTrackingSimple.listMySessions,
    {},
    {
      initialNumItems: 20,
    },
  );

  async function handleDelete(sessionId: (typeof results)[number]["_id"]) {
    const confirmResult = await confirm({
      title: "Delete session?",
      description: "Are you sure you want to delete this session? This action cannot be undone.",
      confirmButtonVariant: "destructive",
      confirmText: "Yes, delete it",
    });

    if (confirmResult) {
      await deleteSession({ sessionId });
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Recent Sessions</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3">
        {status === "LoadingFirstPage" &&
          Array.from({ length: 3 }).map((_, i) => (
            <div
              className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between"
              key={i}
            >
              <div className="grid gap-1">
                <Skeleton className="h-8 w-50" />
                <Skeleton className="h-5 w-80" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-17" />
                <Skeleton className="h-9 w-21" />
              </div>
            </div>
          ))}

        {status !== "LoadingFirstPage" && results.length === 0 && (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            No sessions yet. Press Start to begin tracking.
          </div>
        )}

        {results.map((session) => (
          <div
            key={session._id}
            className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="grid gap-1">
              <div className="font-mono text-2xl font-semibold tracking-tight">
                <StaticDuration
                  startedAt={new Date(session.startedAt)}
                  stoppedAt={session.stoppedAt}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {new Date(session.startedAt).toLocaleString("en-US", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
                {" → "}
                {session.stoppedAt
                  ? new Date(session.stoppedAt).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })
                  : "Ongoing"}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Dialog>
                <DialogTrigger
                  render={
                    <Button type="button" variant="outline" size="sm" className="gap-1.5">
                      <PencilLineIcon className="size-3.5" />
                      Edit
                    </Button>
                  }
                />
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Session</DialogTitle>
                    <DialogDescription>
                      Change the start and end time of this session, or add a reason for the edit.
                    </DialogDescription>
                  </DialogHeader>
                  {/* <EditSessionForm sessionId={session._id} /> */}
                </DialogContent>
              </Dialog>

              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="gap-1.5"
                onClick={() => void handleDelete(session._id)}
              >
                <TrashIcon className="size-3.5" />
                Delete
              </Button>
            </div>
          </div>
        ))}
      </CardContent>

      {(status === "CanLoadMore" || status === "LoadingMore") && (
        <CardFooter className="justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={status === "LoadingMore"}
            onClick={() => loadMore(20)}
          >
            {status === "LoadingMore" && <Loader2Icon className="size-4 animate-spin" />}
            {status === "LoadingMore" ? "Loading..." : "Load more"}
          </Button>
        </CardFooter>
      )}

      {status === "Exhausted" && results.length !== 0 && (
        <CardFooter className="justify-center">
          <p className="text-sm text-muted-foreground">No more sessions to load.</p>
        </CardFooter>
      )}
    </Card>
  );
}

export function CalendarWithTime() {
  const [date, setDate] = React.useState<Date | undefined>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 12),
  );

  return (
    <Card size="sm" className="mx-auto w-fit">
      <CardContent>
        <Calendar mode="single" selected={date} onSelect={setDate} className="p-0" />
      </CardContent>
      <CardFooter className="border-t bg-card">
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="time-from">Time</FieldLabel>
            <InputGroup>
              <InputGroupInput
                id="time"
                type="time"
                step="1"
                defaultValue="10:30:00"
                className="appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
              />
              <InputGroupAddon>
                <Clock2Icon className="text-muted-foreground" />
              </InputGroupAddon>
            </InputGroup>
          </Field>
        </FieldGroup>
      </CardFooter>
    </Card>
  );
}

// function EditSessionForm({ sessionId }: { sessionId: Id<"workSessions"> }) {
//   const editSession = useMutation(api.timeTrackingSimple.editSession);

//   const form = useForm({
//     defaultValues: {
//       title: "",
//       description: "",
//     },
//     validators: {
//       onSubmit: formSchema,
//     },
//     onSubmit: async ({ value }) => {
//       toast("You submitted the following values:", {
//         description: (
//           <pre className="bg-code text-code-foreground mt-2 w-[320px] overflow-x-auto rounded-md p-4">
//             <code>{JSON.stringify(value, null, 2)}</code>
//           </pre>
//         ),
//         position: "bottom-right",
//         classNames: {
//           content: "flex flex-col gap-2",
//         },
//         style: {
//           "--border-radius": "calc(var(--radius)  + 4px)",
//         } as React.CSSProperties,
//       });
//     },
//   });

//   function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
//     event.preventDefault();

//     const formData = new FormData(event.currentTarget);
//     const startedAt = formData.get("startedAt");
//     const stoppedAt = formData.get("stoppedAt");
//     const editReason = formData.get("editReason");

//     if (typeof startedAt !== "string" || typeof stoppedAt !== "string") {
//       // Shouldn't happen since these are required fields, but just in case
//       return;
//     }

//     editSession({
//       sessionId,
//       startedAt: new Date(startedAt).getTime(),
//       stoppedAt: new Date(stoppedAt).getTime(),
//       editReason: typeof editReason === "string" ? editReason : undefined,
//     });
//   }

//   return (
//     <form>
//       <FieldSet>
//         <FieldGroup>
//           <Field>
//             <FieldLabel htmlFor="started-at">Started At</FieldLabel>
//             {/* TODO: Add a date picker with time. */}
//             <CalendarWithTime />
//           </Field>
//           <Field>
//             <FieldLabel htmlFor="stopped-at">Stopped At</FieldLabel>
//             {/* TODO: Add a date picker with time. */}
//             <CalendarWithTime />
//           </Field>
//         </FieldGroup>
//         <FieldGroup>
//           <Field>
//             <FieldLabel htmlFor="edit-reason">Edit Reason</FieldLabel>
//             <Textarea
//               id="edit-reason"
//               name="editReason"
//               placeholder="Reason for editing this session"
//             />
//           </Field>
//         </FieldGroup>
//       </FieldSet>
//       <DialogFooter>
//         <DialogClose render={<Button variant="outline">Cancel</Button>} />
//         <Button type="submit">Save changes</Button>
//       </DialogFooter>
//     </form>
//   );
// }
